import React, { useEffect, useState } from "react";
import { Tabs, Tab, Button } from "@mui/material";
import {
  getPersonas,
  deletePersona,
  createPersona,
  updatePersona,
  getPersonaById,
} from "../../../helper/apicalls/personas";
import {
  getEvalPrompts as getEvals,
  deleteEvalPrompt as deleteEval,
  createEvalPrompt as createEval,
  updateEvalPrompt as updateEval,
  getEvalPromptById as getEvalById,
} from "../../../helper/apicalls/evalPrompts";
import {
  getPrompts,
  getPromptById,
  createPrompt,
  updatePrompt,
  deletePrompt,
} from "../../../helper/apicalls/prompts";
import GlobalLoader from "../../common/GlobalLoader";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import PromptCard from "./PromptCard";
import CreatePersonaPopup from "./CreatePersonaPopup";
import CreateEvalPopup from "./CreateEvalPopup";
import "../../../styles/admin/prompts.scss";

const PromptsPage = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("chatbot");
  const [activeSubTab, setActiveSubTab] = useState("think_analysis_validation");
  const [viewPersona, setViewPersona] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [personaToEdit, setPersonaToEdit] = useState(null);
  const [personaToToggle, setPersonaToToggle] = useState(null);
  const [isToggleConfirmOpen, setIsToggleConfirmOpen] = useState(false);
  const [personaToDelete, setPersonaToDelete] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isActiveDeleteOpen, setIsActiveDeleteOpen] = useState(false);

  const fetchPersonas = async (type, tabOverride) => {
    const currentTab = tabOverride || activeTab;
    try {
      setLoading(true);
      setError(null);
      let data;
      if (currentTab === "eval") {
        data = await getEvals();
      } else if (currentTab === "student" || currentTab === "chatbot") {
        data = await getPrompts(type);
      } else {
        data = await getPersonas(type);
      }
      setPrompts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load prompts");
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveRequest = (prompt) => {
    setPersonaToToggle(prompt);
    setIsToggleConfirmOpen(true);
  };

  const handleConfirmToggleActive = async () => {
    if (!personaToToggle || !personaToToggle.id) {
      setIsToggleConfirmOpen(false);
      return;
    }
    try {
      setLoading(true);
      if (activeTab === "eval") {
        const updatedPayload = {
          default: !personaToToggle.default,
        };
        await updateEval(personaToToggle.id, updatedPayload);
      } else if (activeTab === "student" || activeTab === "chatbot") {
        const updatedPayload = {
          default: !personaToToggle.default,
        };
        await updatePrompt(personaToToggle.id, updatedPayload);
      } else {
        const updatedPayload = {
          name: personaToToggle.name,
          persona: personaToToggle.persona,
          persona_type: personaToToggle.persona_type,
          is_active: !personaToToggle.is_active,
        };
        await updatePersona(personaToToggle.id, updatedPayload);
      }
      if (activeTab === "chatbot" || activeTab === "student") {
        await fetchPersonas(activeSubTab);
      } else if (activeTab === "eval") {
        await fetchPersonas("eval");
      } else {
        await fetchPersonas(activeTab);
      }
    } catch (err) {
      console.error("Error toggling active state:", err);
      setError("Failed to update status");
    } finally {
      setLoading(false);
      setIsToggleConfirmOpen(false);
      setPersonaToToggle(null);
    }
  };

  const handleOpenCreatePersona = () => {
    setPersonaToEdit(null);
    setIsCreateOpen(true);
  };

  const handleCloseCreatePersona = () => {
    setIsCreateOpen(false);
  };

  const handleSavePersona = async (payload) => {
    try {
      setLoading(true);
      if (activeTab === "eval") {
        if (personaToEdit && personaToEdit.id) {
          await updateEval(personaToEdit.id, payload);
        } else {
          await createEval(payload);
        }
      } else if (activeTab === "student" || activeTab === "chatbot") {
        if (personaToEdit && personaToEdit.id) {
          await updatePrompt(personaToEdit.id, payload);
        } else {
          await createPrompt(payload);
        }
      } else {
        if (personaToEdit && personaToEdit.id) {
          await updatePersona(personaToEdit.id, payload);
        } else {
          await createPersona(payload);
        }
      }
      if (activeTab === "chatbot" || activeTab === "student") {
        await fetchPersonas(activeSubTab);
      } else if (activeTab === "eval") {
        await fetchPersonas("eval");
      } else {
        await fetchPersonas(activeTab);
      }
      setIsCreateOpen(false);
      setPersonaToEdit(null);
    } catch (err) {
      console.error("Error saving persona/prompt:", err);
      setError("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === "chatbot") {
      setActiveSubTab("think_analysis_validation");
      fetchPersonas("think_analysis_validation", newValue);
    } else if (newValue === "student") {
      setActiveSubTab("ai_tutor");
      fetchPersonas("ai_tutor", newValue);
    } else if (newValue === "eval") {
      fetchPersonas("eval", newValue);
    } else {
      fetchPersonas(newValue, newValue);
    }
  };

  const handleCardClick = async (prompt) => {
    if (!prompt || !prompt.id) return;
    try {
      setLoading(true);
      if (activeTab === "eval") {
        const fullPrompt = await getEvalById(prompt.id);
        setViewPersona(fullPrompt || prompt);
      } else if (activeTab === "student" || activeTab === "chatbot") {
        const fullPrompt = await getPromptById(prompt.id);
        setViewPersona(fullPrompt || prompt);
      } else {
        const fullPersona = await getPersonaById(prompt.id);
        setViewPersona(fullPersona || prompt);
      }
    } catch (e) {
      console.error("Error fetching persona/prompt by id:", e);
      setViewPersona(prompt);
    } finally {
      setLoading(false);
      setIsViewOpen(true);
    }
  };

  const handleEditPersona = async (prompt) => {
    if (!prompt || !prompt.id) return;
    try {
      setLoading(true);
      if (activeTab === "eval") {
        const fullPrompt = await getEvalById(prompt.id);
        setPersonaToEdit(fullPrompt || prompt);
      } else if (activeTab === "student" || activeTab === "chatbot") {
        const fullPrompt = await getPromptById(prompt.id);
        setPersonaToEdit(fullPrompt || prompt);
      } else {
        const fullPersona = await getPersonaById(prompt.id);
        setPersonaToEdit(fullPersona || prompt);
      }
      setIsCreateOpen(true);
    } catch (err) {
      console.error("Error fetching persona/prompt by id:", err);
      setPersonaToEdit(prompt);
      setIsCreateOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePersona = (prompt) => {
    if (!prompt || !prompt.id) return;
    const isActive = (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? prompt.default : prompt.is_active;
    if (isActive) {
      setIsActiveDeleteOpen(true);
    } else {
      setPersonaToDelete(prompt);
      setIsDeleteConfirmOpen(true);
    }
  };

  const handleConfirmDeletePersona = async () => {
    if (!personaToDelete || !personaToDelete.id) {
      setIsDeleteConfirmOpen(false);
      return;
    }
    try {
      setLoading(true);
      if (activeTab === "eval") {
        await deleteEval(personaToDelete.id);
      } else if (activeTab === "student" || activeTab === "chatbot") {
        await deletePrompt(personaToDelete.id);
      } else {
        await deletePersona(personaToDelete.id);
      }
      if (activeTab === "chatbot" || activeTab === "student") {
        await fetchPersonas(activeSubTab);
      } else if (activeTab === "eval") {
        await fetchPersonas("eval");
      } else {
        await fetchPersonas(activeTab);
      }
    } catch (err) {
      console.error("Error deleting persona/prompt:", err);
      setError("Failed to delete prompt");
    } finally {
      setLoading(false);
      setIsDeleteConfirmOpen(false);
      setPersonaToDelete(null);
    }
  };

  useEffect(() => {
    fetchPersonas("think_analysis_validation", "chatbot");
  }, []);

  useEffect(() => {
    if (
      activeTab === "chatbot" &&
      !["think_analysis_validation", "test_chat_bot"].includes(activeSubTab)
    ) {
      setActiveSubTab("think_analysis_validation");
      fetchPersonas("think_analysis_validation", activeTab);
    } else if (
      activeTab === "student" &&
      !["personalized_quiz", "feedback", "personalized_content", "ai_tutor", "adaptive_learning_content", "active_recall_content"].includes(activeSubTab)
    ) {
      setActiveSubTab("ai_tutor");
      fetchPersonas("ai_tutor", activeTab);
    } else if (activeTab === "eval") {
      fetchPersonas("eval", activeTab);
    }
  }, [activeTab]);

  return (
    <div className="prompts-page">
      <GlobalLoader visible={loading} text="Loading prompts..." />

      <div className="prompts-page__card">
        <div className="prompts-page__header">
          <span>AI Configuration</span>
          
          <Tabs
            className="prompts-page__tabs"
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            {/* <Tab label="AI Tutor" value="ai_tutor" /> */}
            <Tab label="Chatbot" value="chatbot" />
            <Tab label="Student Prompts" value="student" />
            <Tab label="Eval" value="eval" />
          </Tabs>

          <div className="prompts-page__header-right">
            {activeTab !== "eval" && (
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenCreatePersona}
                sx={{
                  fontSize: "var(--fs-sm)",
                  textTransform: "none",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  border: "1px solid var(--color-primary)",
                  backgroundColor: "transparent",
                  color: "var(--color-primary)",
                  fontWeight: 500,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "var(--color-primary)",
                    color: "var(--card-background)",
                    boxShadow: "none",
                  },
                }}
              >
                {(activeTab === "student" || activeTab === "chatbot") ? "Add Prompt" : "Add Persona"}
              </Button>
            )}
          </div>
        </div>

        <div className="prompts-page__content">
          {error && <div className="prompts-page__error">{error}</div>}

          {!error && (
            <>
              {activeTab === "chatbot" && (
                <Tabs
                  className="prompts-page__sub-tabs"
                  value={activeSubTab}
                  onChange={(event, newValue) => {
                    setActiveSubTab(newValue);
                    fetchPersonas(newValue, activeTab);
                  }}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab
                    label="Content Related Chatbot"
                    value="think_analysis_validation"
                  />
                  <Tab label="Test Chatbot" value="test_chat_bot" />
                </Tabs>
              )}

              {activeTab === "student" && (
                <Tabs
                  className="prompts-page__sub-tabs"
                  value={activeSubTab}
                  onChange={(event, newValue) => {
                    setActiveSubTab(newValue);
                    fetchPersonas(newValue, activeTab);
                  }}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab label="AI Tutor" value="ai_tutor" />
                  <Tab label="Personalized Content" value="personalized_content" />
                  <Tab label="Personalized Quiz" value="personalized_quiz" />
                  <Tab label="Feedback" value="feedback" />
                  <Tab label="Adaptive Learning" value="adaptive_learning_content" />
                  <Tab label="Active Recall Content" value="active_recall_content" />
                </Tabs>
              )}

              {!loading && prompts.length === 0 && (
                <div className="prompts-page__empty">No prompts found.</div>
              )}
              {prompts.length > 0 && (
                <div className="prompts-page__grid">
                  {prompts?.map((prompt) => {
                    const displayPrompt = (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? {
                      ...prompt,
                      persona_type: prompt.types,
                      is_active: prompt.default,
                    } : prompt;
                    return (
                      <PromptCard
                        key={prompt.id || prompt.name}
                        prompt={displayPrompt}
                        isEval={activeTab === "eval"}
                        onClick={() => handleCardClick(prompt)}
                        onEdit={() => handleEditPersona(prompt)}
                        onDelete={() => handleDeletePersona(prompt)}
                        onToggleActive={() =>
                          handleToggleActiveRequest(displayPrompt)
                        }
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isViewOpen && (
        (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? (
          <CreateEvalPopup
            open={isViewOpen}
            onClose={() => setIsViewOpen(false)}
            onSave={null}
            evalType={
              activeTab === "student"
                ? activeSubTab
                : activeTab === "chatbot"
                ? activeSubTab
                : undefined
            }
            initialPrompt={viewPersona}
            readOnly
            onEditClick={() => {
              setIsViewOpen(false);
              if (viewPersona) {
                setPersonaToEdit(viewPersona);
                setIsCreateOpen(true);
              }
            }}
          />
        ) : (
          <CreatePersonaPopup
            open={isViewOpen}
            onClose={() => setIsViewOpen(false)}
            onSave={null}
            personaType={activeTab}
            initialPersona={viewPersona}
            readOnly
            onEditClick={() => {
              setIsViewOpen(false);
              if (viewPersona) {
                setPersonaToEdit(viewPersona);
                setIsCreateOpen(true);
              }
            }}
          />
        )
      )}
      {isCreateOpen && (
        (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? (
          <CreateEvalPopup
            open={isCreateOpen}
            onClose={handleCloseCreatePersona}
            onSave={handleSavePersona}
            evalType={
              activeTab === "student"
                ? activeSubTab
                : activeTab === "chatbot"
                ? activeSubTab
                : undefined
            }
            initialPrompt={personaToEdit}
          />
        ) : (
          <CreatePersonaPopup
            open={isCreateOpen}
            onClose={handleCloseCreatePersona}
            onSave={handleSavePersona}
            personaType={activeTab}
            initialPersona={personaToEdit}
          />
        )
      )}
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          title={(activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? "Delete prompt" : "Delete persona"}
          message={
            (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot")
              ? `Are you sure you want to delete the prompt "${
                  personaToDelete?.name || ""
                }"? This action cannot be undone.`
              : `Are you sure you want to delete the persona "${
                  personaToDelete?.name || ""
                }"? This action cannot be undone.`
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          handleDelete={handleConfirmDeletePersona}
        />
      )}
      {isToggleConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isToggleConfirmOpen}
          setIsConfirmDeleteOpen={setIsToggleConfirmOpen}
          title={
            (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? "Toggle Prompt Status" : "Toggle Persona Status"
          }
          message={
            (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot")
              ? (personaToToggle?.default
                  ? `Are you sure you want to deactivate the prompt "${
                      personaToToggle?.name || ""
                    }"?`
                  : `Are you sure you want to activate the prompt "${
                      personaToToggle?.name || ""
                    }"?`)
              : (personaToToggle?.is_active
                  ? `Are you sure you want to deactivate the persona "${
                      personaToToggle?.name || ""
                    }"?`
                  : `Are you sure you want to activate the persona "${
                      personaToToggle?.name || ""
                    }"?`)
          }
          confirmLabel={
            (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot")
              ? (personaToToggle?.default ? "Deactivate" : "Activate")
              : (personaToToggle?.is_active ? "Deactivate" : "Activate")
          }
          cancelLabel="Cancel"
          handleDelete={handleConfirmToggleActive}
        />
      )}
      {isActiveDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isActiveDeleteOpen}
          setIsConfirmDeleteOpen={isActiveDeleteOpen}
          title={(activeTab === "eval" || activeTab === "student" || activeTab === "chatbot") ? "Active prompt cannot be deleted" : "Active persona cannot be deleted"}
          message={
            (activeTab === "eval" || activeTab === "student" || activeTab === "chatbot")
              ? "Please deactivate the prompt before attempting to delete it."
              : "Please deactivate the persona before attempting to delete it."
          }
          confirmLabel="OK"
          cancelLabel=""
          handleDelete={() => setIsActiveDeleteOpen(false)}
        />
      )}
    </div>
  );
};

export default PromptsPage;
